import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Math } from "./Math";
import { useIsMobile } from "@/hooks/use-mobile";

interface CTAConfig {
  calculatorType: string;
  basicQuestion: string;
  aiResponse: {
    summary: string;
    formula?: string;
    calculation?: string;
    insights: string[];
    conclusion: string;
  };
  ctaText?: string;
}

interface CalculatorSuperCalcCTAProps {
  config: CTAConfig;
  trigger?: number | string; // Changes when calculation happens
}

type Stage = "idle" | "typing" | "thinking" | "complete";

export function CalculatorSuperCalcCTA({ config, trigger }: CalculatorSuperCalcCTAProps) {
  const [stage, setStage] = useState<Stage>("idle");
  const [typedText, setTypedText] = useState("");
  const isMobile = useIsMobile();
  
  const fullQuestion = config.basicQuestion;

  // Start animation when trigger changes
  useEffect(() => {
    if (trigger) {
      setStage("typing");
      setTypedText("");
    }
  }, [trigger]);

  useEffect(() => {
    if (stage === "typing") {
      let currentIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentIndex <= fullQuestion.length) {
          setTypedText(fullQuestion.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
          setStage("thinking");
        }
      }, 30);

      return () => clearInterval(typingInterval);
    }
  }, [stage, fullQuestion]);

  useEffect(() => {
    if (stage === "thinking") {
      const thinkingDelay = setTimeout(() => {
        setStage("complete");
      }, 1500);

      return () => clearTimeout(thinkingDelay);
    }
  }, [stage]);


  return (
    <Card className="w-full overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-background to-muted/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          Get More From Your Results with SuperCalc AI
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          See how AI can provide deeper insights beyond the basic calculation
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Question Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <span>You ask:</span>
            </div>
            <Card className="bg-background/50 border-muted min-h-[100px] flex items-center">
              <CardContent className="p-4 w-full">
                <p className="text-sm sm:text-base break-words">
                  {typedText}
                  {stage === "typing" && (
                    <span className="inline-block w-0.5 h-4 bg-primary ml-1 animate-pulse" />
                  )}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Answer Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-primary">
              <Brain className="h-4 w-4" />
              <span>SuperCalc AI answers:</span>
            </div>
            <Card className="bg-primary/5 border-primary/20 min-h-[100px]">
              <CardContent className="p-4 w-full space-y-3">
                {stage === "thinking" && (
                  <div className="flex items-center justify-center h-full min-h-[80px]">
                    <div className="flex items-center gap-2 text-primary">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span className="text-sm font-medium">Analyzing...</span>
                    </div>
                  </div>
                )}

                {stage === "complete" && (
                  <div className="space-y-3 text-sm sm:text-base">
                    <p className="text-foreground">{config.aiResponse.summary}</p>
                    
                    {config.aiResponse.formula && (
                      <div className="bg-background/50 p-3 rounded-md overflow-x-auto">
                        <Math block className="text-xs sm:text-sm">
                          {config.aiResponse.formula}
                        </Math>
                      </div>
                    )}
                    
                    {config.aiResponse.calculation && (
                      <div className="bg-background/50 p-3 rounded-md">
                        <p className="font-mono text-xs sm:text-sm">{config.aiResponse.calculation}</p>
                      </div>
                    )}
                    
                    {config.aiResponse.insights.length > 0 && (
                      <div className="space-y-2">
                        <p className="font-semibold text-primary">Key Insights:</p>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                          {config.aiResponse.insights.map((insight, idx) => (
                            <li key={idx} className="text-xs sm:text-sm">{insight}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <p className="font-medium text-foreground border-t border-border pt-3">
                      {config.aiResponse.conclusion}
                    </p>
                  </div>
                )}

                {(stage === "idle" || stage === "typing") && (
                  <div className="flex items-center justify-center h-full min-h-[80px] text-muted-foreground">
                    <p className="text-sm">Waiting for your question...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
          <Button asChild size={isMobile ? "default" : "lg"} className="flex-1">
            <Link to="/supercalc" className="flex items-center gap-2">
              <Brain className="h-4 w-4" />
              {config.ctaText || "Try SuperCalc (1 Free Query)"}
            </Link>
          </Button>
          <Button asChild size={isMobile ? "default" : "lg"} variant="outline" className="flex-1">
            <Link to="/membership" className="flex items-center gap-2">
              <span>Get Unlimited Access</span>
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
